import { z } from 'zod'

import type { PerformanceLevel } from '@/client'
import {
  EMPTY_PRIMARY_FILTERS,
  primaryFilterSearchSchema,
  pruned,
  type PrimaryFilterValue,
} from '@/lib/filters'

/**
 * The URL state of every advertising page.
 *
 * Optional with `.catch` throughout: a query string is user input, and a bad
 * value falls back to the default instead of failing the page. Defaults are
 * applied at read time and pruned on the way out, so a page's clean URL is
 * its default view. See CLAUDE.md §4.5 for the longer argument.
 *
 * The router parses search values as JSON, so `?storeIds=[1,2]` arrives as
 * an array and `?adGroup=12` as a number.
 *
 * The primary filters — Marketplace, Store, Brand, date range — use the keys
 * every data page shares (`lib/filters`). The keys the pages carried before
 * (`stores`, `store`, `brands`, `start`, `end`, `tab`) are still read, so a
 * link saved last month still opens the same view; `primaryOf` folds them in
 * and a page clears them the first time it writes the new ones.
 */

export { pruned }

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)
const page = z.number().int().positive().optional().catch(undefined)
const ids = z.array(z.number().int()).optional().catch(undefined)
const id = z.number().int().optional().catch(undefined)
const text = z.string().max(200).optional().catch(undefined)
const order = z.enum(['asc', 'desc']).optional().catch(undefined)

/** The keys the pages used before the shared filter bar. */
const legacyPrimary = {
  stores: ids.describe(
    'Legacy store ids, read into storeIds when that is empty; cleared on the first write.',
  ),
  store: id.describe(
    'Legacy single store id, read into storeIds when that is empty; cleared on the first write.',
  ),
  brands: ids.describe(
    'Legacy brand ids, read into brandIds when that is empty; cleared on the first write.',
  ),
  start: day.describe(
    'Legacy first day (YYYY-MM-DD), read as dateFrom when that is unset; cleared on the first write.',
  ),
  end: day.describe(
    'Legacy last day (YYYY-MM-DD), read as dateTo when that is unset; cleared on the first write.',
  ),
}
type LegacyPrimary = z.infer<z.ZodObject<typeof legacyPrimary>>

/** Clears the old keys; spread into the first write of the new ones. */
export const CLEAR_LEGACY: LegacyPrimary = {
  stores: undefined,
  store: undefined,
  brands: undefined,
  start: undefined,
  end: undefined,
}

/** The primary filters, with the old keys honoured where the new ones are unset. */
export function primaryOf(search: PrimaryFilterValue & LegacyPrimary): PrimaryFilterValue {
  const legacyStores = search.stores ?? (search.store !== undefined ? [search.store] : [])
  return {
    ...EMPTY_PRIMARY_FILTERS,
    warehouseIds: search.warehouseIds,
    marketplaceIds: search.marketplaceIds,
    storeIds: search.storeIds.length ? search.storeIds : legacyStores,
    brandIds: search.brandIds.length ? search.brandIds : (search.brands ?? []),
    dateFrom: search.dateFrom ?? search.start,
    dateTo: search.dateTo ?? search.end,
    sku: search.sku,
    asin: search.asin,
  }
}

export const PERFORMANCE_SORTS = [
  'name',
  'impressions',
  'clicks',
  'spend',
  'units',
  'sales',
  'ctr',
  'cpc',
  'conversion',
  'acos',
] as const
const sort = z.enum(PERFORMANCE_SORTS).optional().catch(undefined)

// --- Levels ---------------------------------------------------------------------

/** What a performance page can list: the API's levels, plus the negatives. */
export const AMS_LEVELS = [
  'ad_group',
  'campaign',
  'sku',
  'brand',
  'category',
  'subcategory',
  'asin',
  'target',
  'search_term',
  'negatives',
  'alt_product',
] as const satisfies ReadonlyArray<PerformanceLevel | 'negatives'>
export type AmsLevel = (typeof AMS_LEVELS)[number]

/** Levels that fold ad groups by what the master says about them. */
export const AGGREGATE_LEVELS: ReadonlySet<AmsLevel> = new Set([
  'sku',
  'brand',
  'category',
  'subcategory',
])

/** The tab names the pages used before, and the level each one meant. */
const LEGACY_TABS = {
  ad_groups: 'ad_group',
  campaigns: 'campaign',
  asins: 'asin',
  targets: 'target',
  search_terms: 'search_term',
  negatives: 'negatives',
  alt_products: 'alt_product',
} as const
const legacyTab = z
  .enum(['ad_groups', 'campaigns', 'asins', 'targets', 'search_terms', 'negatives', 'alt_products'])
  .optional()
  .catch(undefined)
  .describe('Legacy tab name, read as the level it meant when level is unset.')

export function levelOf(
  level: AmsLevel | undefined,
  tab: keyof typeof LEGACY_TABS | undefined,
  fallback: AmsLevel = 'ad_group',
): AmsLevel {
  return level ?? (tab ? LEGACY_TABS[tab] : fallback)
}

// --- Trend ----------------------------------------------------------------------

export const TREND_METRICS = ['spend', 'sales', 'impressions', 'clicks', 'units'] as const
export type TrendMetric = (typeof TREND_METRICS)[number]
export const TREND_GRANULARITIES = ['day', 'week', 'month', 'quarter', 'year'] as const
export type TrendGranularity = (typeof TREND_GRANULARITIES)[number]

// --- AMS View ---------------------------------------------------------------

export const amsViewSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  level: z
    .enum(AMS_LEVELS)
    .optional()
    .catch(undefined)
    .describe('What each row is; default ad_group.'),
  tab: legacyTab,
  /** The ad group drilled into, by master id. */
  adGroup: id.describe('The ad group drilled into, by master id.'),
  q: text.describe('Free-text search over the rows.'),
  status: z
    .enum(['ENABLED', 'PAUSED', 'ARCHIVED'])
    .optional()
    .catch(undefined)
    .describe('Ad group status to narrow to; absent means any.'),
  sort: sort.describe('Column the rows are sorted by.'),
  order: order.describe('Sort direction; default desc.'),
  page: page.describe('Page of the table, 1-based; default 1.'),
  metrics: z
    .array(z.enum(TREND_METRICS))
    .optional()
    .catch(undefined)
    .describe('Metrics the trend chart plots; default spend.'),
  granularity: z
    .enum(TREND_GRANULARITIES)
    .optional()
    .catch(undefined)
    .describe('Bucket size of the trend chart; default day.'),
})
export type AmsViewSearch = z.infer<typeof amsViewSearchSchema>

// --- Review AMS Data ----------------------------------------------------------

export const REVIEW_LEVELS = [
  'ad_group',
  'sku',
  'brand',
  'category',
  'subcategory',
  'asin',
  'target',
  'search_term',
  'negatives',
  'alt_product',
] as const satisfies readonly AmsLevel[]
export type ReviewLevel = (typeof REVIEW_LEVELS)[number]

export const reviewSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  compareFrom: day.describe('First day of the comparison period, YYYY-MM-DD.'),
  compareTo: day.describe('Last day of the comparison period, YYYY-MM-DD.'),
  compareStart: day.describe(
    'Legacy first day of the comparison period, read when compareFrom is unset.',
  ),
  compareEnd: day.describe(
    'Legacy last day of the comparison period, read when compareTo is unset.',
  ),
  type: z
    .enum(['SP', 'SB', 'SD'])
    .optional()
    .catch(undefined)
    .describe('Ad type to narrow to; absent means every type.'),
  level: z
    .enum(REVIEW_LEVELS)
    .optional()
    .catch(undefined)
    .describe('What each row is; default ad_group.'),
  tab: legacyTab,
  adGroup: id.describe('The ad group drilled into, by master id.'),
  q: text.describe('Free-text search over the rows.'),
  sort: sort.describe('Column the rows are sorted by.'),
  order: order.describe('Sort direction; default desc.'),
  page: page.describe('Page of the table, 1-based; default 1.'),
})
export type ReviewSearch = z.infer<typeof reviewSearchSchema>

// --- Change history ---------------------------------------------------------------

export const CHANGE_GRAINS = ['ad_groups', 'asins', 'targets', 'search_terms', 'negatives'] as const
export const CHANGE_SORTS = ['timestamp', 'status', 'ad_group', 'entity', 'rule'] as const

const texts = z.array(z.string().max(200)).max(200).optional().catch(undefined)

export const changeHistorySearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  grain: z
    .enum(CHANGE_GRAINS)
    .optional()
    .catch(undefined)
    .describe('Which kind of entity the changes are listed for; default ad_groups.'),
  /** One value each, as links saved before the filters took several still carry. */
  action: text.describe('Legacy single action, read when actions is unset; cleared on write.'),
  status: text.describe('Legacy single outcome, read when statuses is unset; cleared on write.'),
  rule: text.describe('Legacy single rule, read when rules is unset; cleared on write.'),
  actions: texts.describe('Change actions to include; absent means every action.'),
  statuses: texts.describe(
    'Outcomes to include (success, failed, skipped…); absent means every outcome.',
  ),
  rules: texts.describe('Rule codes to include; absent means every rule.'),
  sources: texts.describe(
    'Where the change came from (rules_engine, manual, tickets…); absent means all.',
  ),
  /** People, by user id. */
  actors: ids.describe('People who made the change, by user id; absent includes system changes.'),
  /** Amazon ad group and campaign ids, as a timeline links them. */
  adGroups: texts.describe('Amazon ad group ids to narrow to, as a timeline links them.'),
  campaigns: texts.describe('Amazon campaign ids to narrow to, as a timeline links them.'),
  q: text.describe('Free-text search over the changes.'),
  sort: z
    .enum(CHANGE_SORTS)
    .optional()
    .catch(undefined)
    .describe('Column the changes are sorted by; default timestamp.'),
  order: order.describe('Sort direction; default desc.'),
  page: page.describe('Page of the change list, 1-based, 50 rows a page; default 1.'),
})
export type ChangeHistorySearch = z.infer<typeof changeHistorySearchSchema>

/** The legacy single-value keys; spread into the first write of the lists. */
export const CLEAR_SINGLE_FACETS = {
  action: undefined,
  status: undefined,
  rule: undefined,
} as const

/** A facet's values, the list key first and the legacy single key where it is unset. */
export function facetValues(
  search: ChangeHistorySearch,
  facet: 'actions' | 'statuses' | 'rules',
): string[] {
  const legacy = { actions: search.action, statuses: search.status, rules: search.rule }[facet]
  return search[facet] ?? (legacy ? [legacy] : [])
}

export const TIMELINE_EVENT_FILTERS = ['all', 'bid_change', 'state_change', 'added'] as const
export type TimelineEventFilter = (typeof TIMELINE_EVENT_FILTERS)[number]
export const TIMELINE_VIEWS = ['list', 'chart'] as const
export type TimelineView = (typeof TIMELINE_VIEWS)[number]

/** What a timeline page shows: which events, and as a list or a chart. */
const timelineDisplay = {
  events: z
    .enum(TIMELINE_EVENT_FILTERS)
    .optional()
    .catch(undefined)
    .describe('Which events the timeline shows; default all.'),
  view: z
    .enum(TIMELINE_VIEWS)
    .optional()
    .catch(undefined)
    .describe('Show the timeline as a list or a chart; default list.'),
}
const amazonId = z.string().max(64).optional().catch(undefined)

export const timelineSearchSchema = z.object({
  store: id.describe('The store the entity belongs to, by id. Required: without it nothing loads.'),
  /** Narrows a target looked up by text to one ad group (Amazon id). */
  adGroup: amazonId.describe(
    'Amazon ad group id that narrows a target looked up by text; unused for ad groups and campaigns.',
  ),
  adType: z
    .enum(['SP', 'SB', 'SD'])
    .optional()
    .catch(undefined)
    .describe('Ad type of the target; unused for ad groups and campaigns.'),
  ...timelineDisplay,
})
export type TimelineSearch = z.infer<typeof timelineSearchSchema>

export const searchTermHistorySearchSchema = z.object({
  store: id.describe('The store the search term ran in, by id.'),
  adGroup: amazonId.describe('Amazon id of the ad group the search term ran under. Required.'),
  term: z
    .string()
    .max(500)
    .optional()
    .catch(undefined)
    .describe('The search term shown. Required.'),
  keywordId: amazonId.describe('Amazon id of the keyword that matched the term.'),
  matchType: z
    .string()
    .max(32)
    .optional()
    .catch(undefined)
    .describe('Match type of that keyword (EXACT, PHRASE, BROAD…).'),
  keyword: z
    .string()
    .max(500)
    .optional()
    .catch(undefined)
    .describe('Text of the keyword that matched the term.'),
  ...timelineDisplay,
})
export type SearchTermHistorySearch = z.infer<typeof searchTermHistorySearchSchema>

// --- Search analytics -----------------------------------------------------------------

export const searchAnalyticsSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  show: z
    .enum(['all', 'with_marketing', 'without_marketing'])
    .optional()
    .catch(undefined)
    .describe('Which queries are listed: all, only with ads data, or only without; default all.'),
  orderBy: z
    .enum(['impressions', 'clicks', 'purchases'])
    .optional()
    .catch(undefined)
    .describe('Measure the queries are ranked by; default impressions.'),
  source: z
    .enum(['total', 'our', 'marketing'])
    .optional()
    .catch(undefined)
    .describe(
      'Whose figures rank the queries: the market, our ASINs or our ads; default marketing.',
    ),
  page: page.describe('Page of the query list, 1-based, 50 rows a page; default 1.'),
})
export type SearchAnalyticsSearch = z.infer<typeof searchAnalyticsSearchSchema>

// --- Focus search terms ---------------------------------------------------------------

export const focusSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  view: z
    .enum(['sheet', 'history'])
    .optional()
    .catch(undefined)
    .describe('Sheet of one week, or Snapshot History of one term; default sheet.'),
  week: day.describe(
    'First day (YYYY-MM-DD) of the frozen week the sheet shows; default the latest.',
  ),
  q: text.describe('Search over term, ASIN or title on the sheet.'),
  sort: z
    .string()
    .regex(/^[a-z_]{1,40}$/)
    .optional()
    .catch(undefined)
    .describe(
      'Sheet column the terms are sorted by: search_term, impressions, clicks, spend or sales; default spend.',
    ),
  order: order.describe('Sort direction; default desc.'),
  page: page.describe('Page of the sheet, 1-based; default 1.'),
  /** Rows per page of the sheet. */
  size: z
    .union([z.literal(10), z.literal(25), z.literal(50)])
    .optional()
    .catch(undefined)
    .describe('Rows per page of the sheet; default 25.'),
  /** The term the history view shows. */
  term: z
    .string()
    .max(512)
    .optional()
    .catch(undefined)
    .describe('The search term the history view shows.'),
  /** Expanded rows: terms on the sheet, weeks in the history. */
  open: z
    .array(z.string().max(512))
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Expanded rows: terms on the sheet, weeks (YYYY-MM-DD) in the history.'),
  /** The term whose earlier bid actions are open, from the eye beside it on the sheet. */
  past: z
    .string()
    .max(512)
    .optional()
    .catch(undefined)
    .describe('The term whose earlier bid actions dialog is open.'),
})
export type FocusSearch = z.infer<typeof focusSearchSchema>

// --- Report sync ------------------------------------------------------------------------

export const reportSyncSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  dateField: z
    .enum(['created_at', 'last_updated_at', 'report_start_date', 'report_end_date'])
    .optional()
    .catch(undefined)
    .describe('Which date of a report pull the date range applies to; default created_at.'),
  types: z
    .array(z.string())
    .optional()
    .catch(undefined)
    .describe('Report types to include; absent means every type.'),
  status: z
    .enum(['successful', 'failed', 'pending'])
    .optional()
    .catch(undefined)
    .describe('Status tab: only successful, failed or pending pulls; absent means all.'),
  page: page.describe('Page of the pull list, 1-based, 50 rows a page; default 1.'),
})
export type ReportSyncSearch = z.infer<typeof reportSyncSearchSchema>

// --- AdGroup master and New Targeting ------------------------------------------------------

export const adGroupMasterSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  q: text.describe('Search over the ad group master rows.'),
  page: page.describe('Page of the ad group list, 1-based, 50 rows a page; default 1.'),
})
export type AdGroupMasterSearch = z.infer<typeof adGroupMasterSearchSchema>

export const newTargetingSearchSchema = primaryFilterSearchSchema().extend({
  ...legacyPrimary,
  /** Amazon's ad-group id. */
  adGroup: z
    .string()
    .max(64)
    .optional()
    .catch(undefined)
    .describe('Amazon id of the ad group the targets are priced for.'),
})
export type NewTargetingSearch = z.infer<typeof newTargetingSearchSchema>
